import { useState } from "react";
import { Params, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { PaymentLinkData, SimpleErrorData } from "app/types.ts";
import { molliePlatformApiUrl } from "app/constants";
import { useEffectOnce } from "react-use";
import { UnexpectedErrorState } from "app/components";
import { messages } from "app/i18n";
import {
  ExpectedErrorState,
  StateWithFooter,
} from "@mollie/revenue-collection-components";

enum Error {
  BadResponse = "Bad response from server",
  InvoiceIsNotPayable = "Invoice is not payable",
  NoSupportedPaymentMethodsAvailable = "No supported payment methods available",
}

export const InvoicePaymentPage = () => {
  const intl = useIntl();
  const [paymentLinkData, setPaymentLinkData] =
    useState<PaymentLinkData | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const params: Params<string> = useParams();

  useEffectOnce(() => {
    const apiUrl = new URL(
      `invoice-ar/invoice/${params.invoiceReferenceId}/payment-link`,
      molliePlatformApiUrl,
    );

    fetch(apiUrl)
      .then((response) => {
        if (!response.ok) {
          if (response.status === 422) {
            return response.json().then((data: SimpleErrorData) => {
              if (Object.values(Error).includes(data.error as Error)) {
                return setError(data.error as Error);
              }
            });
          }

          return setError(Error.BadResponse);
        }

        return response.json().then((data: PaymentLinkData) => {
          setPaymentLinkData(data);
        });
      })
      .catch(() => {
        return setError(Error.BadResponse);
      });
  });

  if (error === Error.BadResponse) {
    return <UnexpectedErrorState />;
  }

  if (error) {
    const errorKey = Object.keys(Error).find(
      (key) => Error[key as keyof typeof Error] === error,
    );

    return (
      <ExpectedErrorState
        error={intl.formatMessage(
          messages[`invoicePaymentError${errorKey}` as keyof typeof messages],
        )}
        description={intl.formatMessage(
          messages[
            `invoicePaymentError${errorKey}Description` as keyof typeof messages
          ],
        )}
      />
    );
  }

  if (!paymentLinkData) {
    return (
      <StateWithFooter
        title={intl.formatMessage(messages.invoiceLandingLoadingTitle)}
        variant="loading"
        description={intl.formatMessage(messages.invoiceLandingLoadingMessage)}
      />
    );
  }

  // deepcode ignore OR: this URL is supplied by the backend
  window.location.replace(paymentLinkData.paymentLinkUrl);
};
